<script setup lang="ts">
import { onMounted, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import { usePoleDevStore, type SprintStats } from '@/stores/usePoleDevStore'

const store = usePoleDevStore()

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10)
}
function defaultStart() {
  const d = new Date()
  d.setUTCMonth(d.getUTCMonth() - 3)
  d.setUTCHours(0, 0, 0, 0)
  return isoDate(d)
}
function defaultEnd() {
  return isoDate(new Date())
}

const from = ref(defaultStart())
const to = ref(defaultEnd())
const completedStatusesInput = ref('Fini')
const stats = ref<SprintStats[]>([])
const error = ref<string | null>(null)
const loading = ref(false)

async function load() {
  loading.value = true
  error.value = null
  try {
    const completed = completedStatusesInput.value
      ? completedStatusesInput.value.split(',').map((s) => s.trim()).filter(Boolean)
      : undefined
    stats.value = await store.fetchSprintStats({ from: from.value, to: to.value, completedStatuses: completed })
  } catch (e) {
    error.value = (e as Error).message
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="p-6 flex flex-col gap-6">
    <div class="flex items-center justify-between flex-wrap gap-3">
      <Bs2Title title="Pôle dev — sprints GitHub" subtitle="Stats par sprint sur le project backlog" />
    </div>

    <div class="card bg-base-100 shadow p-4 flex flex-wrap items-end gap-3">
      <label class="form-control">
        <span class="label-text text-xs mb-1">Du</span>
        <input v-model="from" type="date" class="input input-bordered input-sm" />
      </label>
      <label class="form-control">
        <span class="label-text text-xs mb-1">Au</span>
        <input v-model="to" type="date" class="input input-bordered input-sm" />
      </label>
      <label class="form-control">
        <span class="label-text text-xs mb-1">Statuts "complétés" (séparés par virgule)</span>
        <input v-model="completedStatusesInput" type="text" class="input input-bordered input-sm w-64" placeholder="Fini" />
      </label>
      <button class="btn btn-sm" :disabled="loading" @click="load">Charger</button>
      <span v-if="loading" class="text-sm text-base-content/60">Chargement…</span>
      <span v-if="error" class="text-sm text-error">Erreur : {{ error }}</span>
    </div>

    <div v-if="stats.length === 0 && !loading" class="text-sm text-base-content/60 text-center py-12">
      Aucun sprint sur la période sélectionnée.
    </div>

    <div v-for="sp in stats" :key="sp.sprintTitle" class="card bg-base-100 shadow p-4">
      <div class="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <h3 class="font-semibold">{{ sp.sprintTitle }}</h3>
          <div class="text-xs text-base-content/60">
            Début : {{ sp.startDate }} · Durée : {{ sp.duration }} j
          </div>
        </div>
        <div class="text-right">
          <div class="text-2xl font-semibold">{{ sp.completionRate }}%</div>
          <div class="text-xs text-base-content/60">{{ sp.completedCards }}/{{ sp.totalCards }} cards</div>
        </div>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
        <div class="card bg-base-50 p-2">
          <div class="text-xs text-base-content/60">Charge totale</div>
          <div class="font-semibold">{{ sp.totalEstimate }} pts</div>
        </div>
        <div class="card bg-base-50 p-2">
          <div class="text-xs text-base-content/60">Complétée</div>
          <div class="font-semibold">{{ sp.completedEstimate }} pts</div>
        </div>
        <div class="card bg-base-50 p-2">
          <div class="text-xs text-base-content/60">Statuts</div>
          <div class="text-xs">
            <span v-for="(count, status) in sp.statusCounts" :key="status" class="badge badge-ghost badge-xs mr-1">
              {{ status }} : {{ count }}
            </span>
          </div>
        </div>
      </div>

      <details>
        <summary class="cursor-pointer font-semibold text-sm">Détail par dev ({{ sp.users.length }})</summary>
        <table class="table table-sm mt-2">
          <thead>
            <tr>
              <th>Dev</th>
              <th>Cards</th>
              <th>Charge</th>
              <th>Taux</th>
              <th>Statuts</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in sp.users" :key="u.login">
              <td class="font-mono text-xs">{{ u.login }}</td>
              <td>{{ u.cardCount }}</td>
              <td>{{ u.totalEstimate }}</td>
              <td>{{ u.completionRate }}%</td>
              <td>
                <span v-for="(count, status) in u.statusCounts" :key="status" class="badge badge-outline badge-xs mr-1">
                  {{ status }}: {{ count }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </details>
    </div>
  </div>
</template>
